import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { StripeProduct, StripeProductsResponse } from '../../types/stripe';
import api from '../api';
import { asArray, isRecord } from '../payloadGuards';

// Types
interface StripeState {
  products: StripeProduct[];
  selectedProduct: StripeProduct | null;
  isLoading: boolean;
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  lastFetched: number | null;
}

// Initial state
const initialState: StripeState = {
  products: [],
  selectedProduct: null,
  isLoading: false,
  error: null,
  status: 'idle',
  lastFetched: null,
};

// Async thunks
export const fetchStripeProducts = createAsyncThunk(
  'stripe/fetchStripeProducts',
  async (params?: { active?: boolean; limit?: number }, { rejectWithValue }) => {
    try {
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params?.active !== undefined) {
        queryParams.append('active', params.active.toString());
      }
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }
      
      const url = `/stripe/products${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await api.get(url);
      
      
      // Parse the response - body might be a stringified JSON
      let parsedData: StripeProductsResponse | StripeProduct[];
      
      if (response.data?.body) {
        // If body is a string, parse it
        if (typeof response.data.body === 'string') {
          try {
            parsedData = JSON.parse(response.data.body);
          } catch (parseError) {
            console.error('❌ Error parsing body string:', parseError);
            return rejectWithValue('Failed to parse response body');
          }
        } else {
          // Body is already an object
          parsedData = response.data.body;
        }
      } else {
        // No body field, use response.data directly
        parsedData = response.data;
      }
      
      
      // Handle different response formats
      let products: StripeProduct[];
      
      if (Array.isArray(parsedData)) {
        // Direct array of products
        products = parsedData;
      } else if (parsedData.products && Array.isArray(parsedData.products)) {
        // Response with products array
        products = parsedData.products;
      } else if (parsedData.data && Array.isArray(parsedData.data)) {
        // Response with data array
        products = parsedData.data;
      } else {
        console.error('❌ Invalid response format. Parsed data:', parsedData);
        return rejectWithValue('Invalid response format from server');
      }
      
      return products;
    } catch (error: any) {
      console.error('❌ Error fetching Stripe products:', error);
      
      // Handle Axios errors
      if (error.response) {
        const errorData = error.response.data;
        const errorMessage = errorData?.error || errorData?.message || `Failed to fetch Stripe products: ${error.response.status}`;
        console.error('❌ Error response:', errorData);
        return rejectWithValue(errorMessage);
      } else if (error.request) {
        console.error('❌ No response received. Possible network error.');
        return rejectWithValue('Network error: Could not reach server.');
      } else {
        return rejectWithValue(error.message || 'Failed to fetch Stripe products');
      }
    }
  }
);

export const fetchStripeProductById = createAsyncThunk(
  'stripe/fetchStripeProductById',
  async (productId: string, { rejectWithValue }) => {
    try {
      
      const response = await api.get(`/stripe/products/${productId}`);
      
      
      // Parse the response - body might be a stringified JSON
      let parsedData: StripeProduct;
      
      if (response.data?.body) {
        if (typeof response.data.body === 'string') {
          try {
            parsedData = JSON.parse(response.data.body);
          } catch (parseError) {
            console.error('❌ Error parsing body string:', parseError);
            return rejectWithValue('Failed to parse response body');
          }
        } else {
          parsedData = response.data.body;
        }
      } else {
        parsedData = response.data;
      }
      
      // Handle nested product data
      if (parsedData.product) {
        parsedData = parsedData.product;
      }
      
      return parsedData;
    } catch (error: any) {
      console.error('❌ Error fetching Stripe product:', error);
      
      if (error.response) {
        const errorData = error.response.data;
        const errorMessage = errorData?.error || errorData?.message || `Failed to fetch product: ${error.response.status}`;
        return rejectWithValue(errorMessage);
      } else if (error.request) {
        return rejectWithValue('Network error: Could not reach server.');
      } else {
        return rejectWithValue(error.message || 'Failed to fetch Stripe product');
      }
    }
  }
);

// Slice
const stripeSlice = createSlice({
  name: 'stripe',
  initialState,
  reducers: {
    setSelectedProduct: (state, action) => {
      state.selectedProduct = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearProducts: (state) => {
      state.products = [];
      state.selectedProduct = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch products
    builder
      .addCase(fetchStripeProducts.pending, (state) => {
        state.isLoading = true;
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchStripeProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = 'succeeded';
        state.products = asArray(action.payload);
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(fetchStripeProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.status = 'failed';
        state.error = action.payload as string;
      });
    
    // Fetch product by ID
    builder
      .addCase(fetchStripeProductById.pending, (state) => {
        state.isLoading = true;
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchStripeProductById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = 'succeeded';
        state.selectedProduct = action.payload;
        // Everything below dereferences the payload, so bail before it throws.
        if (!isRecord(action.payload)) return;
        // Add to products array if not already present
        const existingIndex = state.products.findIndex(p => p.id === action.payload.id);
        if (existingIndex >= 0) {
          state.products[existingIndex] = action.payload;
        } else {
          state.products.push(action.payload);
        }
        state.error = null;
      })
      .addCase(fetchStripeProductById.rejected, (state, action) => {
        state.isLoading = false;
        state.status = 'failed';
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const {
  setSelectedProduct,
  clearError,
  clearProducts,
} = stripeSlice.actions;

// Export selectors
export const selectStripeProducts = (state: { stripe: StripeState }) =>
  state.stripe.products;
export const selectSelectedStripeProduct = (state: { stripe: StripeState }) =>
  state.stripe.selectedProduct;
export const selectStripeLoading = (state: { stripe: StripeState }) =>
  state.stripe.isLoading;
export const selectStripeError = (state: { stripe: StripeState }) =>
  state.stripe.error;
export const selectStripeStatus = (state: { stripe: StripeState }) =>
  state.stripe.status;
export const selectStripeLastFetched = (state: { stripe: StripeState }) =>
  state.stripe.lastFetched;

// Export reducer
export default stripeSlice.reducer;

